import { useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  GUARD_APP_CLOSE_REASONS,
  GUARD_APP_CLOSE_REASON_COPY,
  applyQueueFilter,
  closeGuardAppAccessRequest,
  markGuardAppStoreAdded,
  queueStatusChip,
  type GuardAppCloseReason,
  type PlatformGuardAppAccessRow,
  type QueueFilter,
} from "../data/guard-app-access";
import { humanizeLabel } from "../data/alert-queue";
import { formatDisplayTime, formatRelativeTime } from "../lib/display-time";

// Platform -> Access queue (founder desk lock 2026-08-09). The founder's real motion is:
// copy a store address, paste it into App Store Connect or Play Console by hand, come back,
// mark it. Copy is therefore the primary verb and sits inline on every address. Marking added
// is a personal claim about work done in another console, so there is deliberately NO bulk
// mark: one request at a time, through the row button or the detail drawer.
//
// Green appears on Installed only - the state the device reports rather than one we claim.
// Amber is attention, not alarm: Part added is amber because it is genuinely half finished;
// a stale Added row gets an amber sub line, not an amber chip, because the delay belongs to
// the store and the officer. Closed rows stay: they are the record of a decision.

const STALE_ADDED_MS = 3 * 24 * 60 * 60 * 1000;

const FILTERS: ReadonlyArray<{ key: QueueFilter; label: string }> = [
  { key: "mine", label: "Waiting on me" },
  { key: "ios", label: "iOS" },
  { key: "android", label: "Android" },
  { key: "all", label: "All companies" },
];

function accountTail(accountId: string): string {
  return `acct ${accountId.slice(0, 4)}`;
}

/** The latest store mark, for "Added" sub-lines: which store emailed them, and when. */
function latestMark(row: PlatformGuardAppAccessRow): { store: string; at: string } | null {
  const marks: Array<{ store: string; at: string }> = [];
  if (row.appleAddedAt !== null) marks.push({ store: "Apple", at: row.appleAddedAt });
  if (row.googleAddedAt !== null) marks.push({ store: "Google", at: row.googleAddedAt });
  if (marks.length === 0) return null;
  marks.sort((a, b) => b.at.localeCompare(a.at));
  return marks[0]!;
}

export function PlatformAccessQueue({
  client,
  rows,
  loading,
  loadError,
  selfUserId,
  timeZone,
  onChanged,
}: {
  client: SupabaseClient;
  rows: readonly PlatformGuardAppAccessRow[];
  loading: boolean;
  loadError: string | null;
  /** The signed-in founder - "by you" renders only for their own recorded marks. */
  selfUserId: string;
  timeZone: string;
  onChanged: () => void;
}) {
  const [filter, setFilter] = useState<QueueFilter>("mine");
  const [openRequestId, setOpenRequestId] = useState<string | null>(null);
  const nowMs = Date.now();

  if (loading && rows.length === 0) {
    return <p className="surface__state" role="status">Loading the access queue…</p>;
  }
  if (loadError !== null) {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {loadError}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="pfd-queue-empty">
        <span className="pfd-queue-empty__check" aria-hidden="true">
          ✓
        </span>
        <h3>Queue is clear.</h3>
        <p>
          Nothing is waiting on you. New requests from company admins land here first, and the tab
          keeps a count so you do not have to come looking.
        </p>
      </div>
    );
  }

  const filtered = applyQueueFilter(rows, filter);
  const openRow = rows.find((row) => row.requestId === openRequestId) ?? null;

  return (
    <div className="pfd-queue">
      <div className="pfd-filters" role="group" aria-label="Filter requests">
        <span className="pfd-filters__label">Show</span>
        {FILTERS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className="pfd-filter"
            aria-pressed={filter === entry.key}
            onClick={() => setFilter(entry.key)}
          >
            {entry.label} <span className="pfd-filter__count">{applyQueueFilter(rows, entry.key).length}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="surface__state" role="status">
          No requests match that filter.
        </p>
      ) : (
        <table className="pfd-table">
          <thead>
            <tr>
              <th scope="col">Requested</th>
              <th scope="col">Company</th>
              <th scope="col">Officer</th>
              <th scope="col">Store account</th>
              <th scope="col">Status</th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <QueueRow
                key={row.requestId}
                client={client}
                row={row}
                nowMs={nowMs}
                selfUserId={selfUserId}
                timeZone={timeZone}
                onChanged={onChanged}
                onOpen={() => setOpenRequestId(row.requestId)}
              />
            ))}
          </tbody>
        </table>
      )}

      {openRow !== null ? (
        <PlatformAccessDetailDrawer
          client={client}
          row={openRow}
          nowMs={nowMs}
          selfUserId={selfUserId}
          timeZone={timeZone}
          onChanged={onChanged}
          onClose={() => setOpenRequestId(null)}
        />
      ) : null}
    </div>
  );
}

function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`pfd-copy${copied ? " pfd-copy--done" : ""}`}
      aria-label={`Copy ${email}`}
      onClick={() => {
        try {
          void navigator.clipboard?.writeText(email);
        } catch {
          // The visual confirmation still tells the founder the address is selectable inline.
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function StoreLines({ row }: { row: PlatformGuardAppAccessRow }) {
  return (
    <>
      {row.appleStoreEmail !== null ? (
        <span className="pfd-store-line">
          <span className="pfd-store-badge">iOS</span>
          <span className="pfd-store-mail">{row.appleStoreEmail}</span>
          <CopyEmailButton email={row.appleStoreEmail} />
        </span>
      ) : null}
      {row.googleStoreEmail !== null ? (
        <span className="pfd-store-line">
          <span className="pfd-store-badge">Android</span>
          <span className="pfd-store-mail">{row.googleStoreEmail}</span>
          <CopyEmailButton email={row.googleStoreEmail} />
        </span>
      ) : null}
    </>
  );
}

/** The one outstanding mark when exactly one store still needs the founder, else null. */
function singleOutstandingMark(row: PlatformGuardAppAccessRow): { store: "apple" | "google"; label: string } | null {
  if (row.status === "requested") {
    if (row.platform === "ios") return { store: "apple", label: "Mark added" };
    if (row.platform === "android") return { store: "google", label: "Mark added" };
    return null; // both stores outstanding: the drawer is the ergonomic for two claims
  }
  if (row.status === "part_added") {
    return row.appleAddedAt === null
      ? { store: "apple", label: "Mark iOS" }
      : { store: "google", label: "Mark Android" };
  }
  return null;
}

function statusSubLine(
  row: PlatformGuardAppAccessRow,
  nowMs: number,
  selfUserId: string,
  timeZone: string,
): { text: string; late: boolean } | null {
  if (row.status === "part_added") {
    const done = row.appleAddedAt !== null ? "iOS" : "Android";
    const doneAt = row.appleAddedAt ?? row.googleAddedAt;
    const doneBy = row.appleAddedAt !== null ? row.appleAddedByUserId : row.googleAddedByUserId;
    const byYou = doneBy !== null && doneBy === selfUserId ? " by you" : "";
    const still = row.appleAddedAt !== null ? "Android" : "iOS";
    return {
      text: `${done} added${byYou} ${formatRelativeTime(doneAt, nowMs, timeZone)}. ${still} still open.`,
      late: false,
    };
  }
  if (row.status === "added" && !row.installed) {
    const mark = latestMark(row);
    if (mark === null) return null;
    const late = nowMs - new Date(mark.at).getTime() > STALE_ADDED_MS;
    if (late) {
      return {
        text: "No install yet. Worth telling the admin to check their spam.",
        late: true,
      };
    }
    return {
      text: `${mark.store} emailed them ${formatRelativeTime(mark.at, nowMs, timeZone)}. Nothing to do here.`,
      late: false,
    };
  }
  if (row.installed) {
    const version = row.installedAppVersion ?? "a Guard build";
    return {
      text: `Phone checked in with ${row.installedAppVersion !== null ? `Guard ${version}` : version}, ${formatRelativeTime(row.installedSeenAt, nowMs, timeZone)}.`,
      late: false,
    };
  }
  if (row.status === "closed") {
    const reason = row.closedReason !== null ? GUARD_APP_CLOSE_REASON_COPY[row.closedReason] : "Closed";
    const byYou = row.closedByUserId !== null && row.closedByUserId === selfUserId ? " by you" : "";
    return {
      text: `${reason}. Closed${byYou} ${formatRelativeTime(row.closedAt, nowMs, timeZone)}.`,
      late: false,
    };
  }
  return null;
}

function QueueRow({
  client,
  row,
  nowMs,
  selfUserId,
  timeZone,
  onChanged,
  onOpen,
}: {
  client: SupabaseClient;
  row: PlatformGuardAppAccessRow;
  nowMs: number;
  selfUserId: string;
  timeZone: string;
  onChanged: () => void;
  onOpen: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const chip = queueStatusChip(row);
  const sub = statusSubLine(row, nowMs, selfUserId, timeZone);
  const outstanding = singleOutstandingMark(row);

  async function mark(store: "apple" | "google"): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await markGuardAppStoreAdded(client, row.requestId, store);
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason);
      return;
    }
    onChanged();
  }

  return (
    <tr>
      <td className="pfd-age">{formatRelativeTime(row.requestedAt, nowMs, timeZone)}</td>
      <td>
        <span className="pfd-co">{row.accountName}</span>
        <span className="pfd-tail">{accountTail(row.accountId)}</span>
      </td>
      <td>
        <span className="pfd-name">{row.memberDisplayName ?? "Name unknown"}</span>
        {row.memberEmail !== null ? <span className="pfd-mail">{row.memberEmail}</span> : null}
      </td>
      <td>
        <StoreLines row={row} />
      </td>
      <td>
        <span className={`gaa-chip gaa-chip--${chip.tone}`}>{chip.label}</span>
        {sub !== null ? (
          <span className={`pfd-sub${sub.late ? " pfd-sub--late" : ""}`}>{sub.text}</span>
        ) : null}
        {failure !== null ? (
          <span className="pfd-sub pfd-sub--late" role="alert">
            {failure}
          </span>
        ) : null}
      </td>
      <td className="pfd-actions">
        {outstanding !== null ? (
          <button
            type="button"
            className="pfd-mark"
            disabled={busy}
            onClick={() => void mark(outstanding.store)}
          >
            {busy ? "Marking…" : outstanding.label}
          </button>
        ) : null}
        <button type="button" className="pfd-open" onClick={onOpen}>
          Open
        </button>
      </td>
    </tr>
  );
}

/**
 * Request detail: the same copy-then-mark motion, one store per row, plus the note, the log,
 * and close-with-reason. Fail closed: Close request stays disabled until a reason is chosen.
 */
function PlatformAccessDetailDrawer({
  client,
  row,
  nowMs,
  selfUserId,
  timeZone,
  onChanged,
  onClose,
}: {
  client: SupabaseClient;
  row: PlatformGuardAppAccessRow;
  nowMs: number;
  selfUserId: string;
  timeZone: string;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState<GuardAppCloseReason | null>(null);

  async function run(action: () => Promise<{ ok: boolean; reason?: string }>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    const outcome = await action();
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.reason ?? "Couldn't record that - please try again.");
      return;
    }
    onChanged();
  }

  const requesterRole = row.requestedByRole !== null ? `, ${humanizeLabel(row.requestedByRole).toLowerCase()}` : "";
  const requester = row.requestedByDisplayName ?? "an admin";

  return (
    <div className="pfd-drawer-holder">
      <button type="button" className="pfd-scrim" aria-label="Close request detail" onClick={onClose} />
      <aside className="pfd-drawer" role="dialog" aria-label={`Access request for ${row.memberDisplayName ?? "member"}`}>
        <header className="pfd-drawer__head">
          <p className="pfd-drawer__eyebrow">Access request</p>
          <h3 className="pfd-drawer__title">{row.memberDisplayName ?? "Name unknown"}</h3>
          <p className="pfd-drawer__sub">
            {row.accountName} · requested {formatRelativeTime(row.requestedAt, nowMs, timeZone)} by{" "}
            {requester}
            {requesterRole}
          </p>
          <button type="button" className="pfd-drawer__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="pfd-drawer__body">
          <section className="pfd-drawer__block">
            <h4 className="pfd-drawer__label">Store accounts</h4>
            <StoreStepRow
              show={row.appleStoreEmail !== null}
              badge="iOS"
              email={row.appleStoreEmail}
              addedAt={row.appleAddedAt}
              addedByYou={row.appleAddedByUserId !== null && row.appleAddedByUserId === selfUserId}
              storeName="Apple"
              markLabel="Mark iOS added"
              closed={row.status === "closed"}
              busy={busy}
              timeZone={timeZone}
              onMark={() => void run(() => markGuardAppStoreAdded(client, row.requestId, "apple"))}
            />
            <StoreStepRow
              show={row.googleStoreEmail !== null}
              badge="Android"
              email={row.googleStoreEmail}
              addedAt={row.googleAddedAt}
              addedByYou={row.googleAddedByUserId !== null && row.googleAddedByUserId === selfUserId}
              storeName="Google"
              markLabel="Mark Android added"
              closed={row.status === "closed"}
              busy={busy}
              timeZone={timeZone}
              onMark={() => void run(() => markGuardAppStoreAdded(client, row.requestId, "google"))}
            />
          </section>

          {row.note !== null ? (
            <section className="pfd-drawer__block">
              <h4 className="pfd-drawer__label">Note from the admin</h4>
              <blockquote className="pfd-quote">{row.note}</blockquote>
            </section>
          ) : null}

          <section className="pfd-drawer__block">
            <h4 className="pfd-drawer__label">Log</h4>
            <ul className="pfd-log">
              <li>
                <span className="pfd-log__time">{formatDisplayTime(row.requestedAt, timeZone)}</span>{" "}
                <strong>Requested</strong> by {requester}
                {requesterRole}. Platform: {row.platform}.
              </li>
              {row.appleAddedAt !== null ? (
                <li>
                  <span className="pfd-log__time">{formatDisplayTime(row.appleAddedAt, timeZone)}</span>{" "}
                  <strong>iOS added</strong>
                  {row.appleAddedByUserId === selfUserId ? " by you" : ""} in App Store Connect.
                </li>
              ) : null}
              {row.googleAddedAt !== null ? (
                <li>
                  <span className="pfd-log__time">{formatDisplayTime(row.googleAddedAt, timeZone)}</span>{" "}
                  <strong>Android added</strong>
                  {row.googleAddedByUserId === selfUserId ? " by you" : ""} in Play Console.
                </li>
              ) : null}
              {row.installedSeenAt !== null ? (
                <li>
                  <span className="pfd-log__time">{formatDisplayTime(row.installedSeenAt, timeZone)}</span>{" "}
                  <strong>Installed</strong>: the phone checked in
                  {row.installedAppVersion !== null ? ` running Guard ${row.installedAppVersion}` : ""}.
                </li>
              ) : null}
              {row.closedAt !== null ? (
                <li>
                  <span className="pfd-log__time">{formatDisplayTime(row.closedAt, timeZone)}</span>{" "}
                  <strong>Closed</strong>
                  {row.closedByUserId === selfUserId ? " by you" : ""}
                  {row.closedReason !== null
                    ? `: ${GUARD_APP_CLOSE_REASON_COPY[row.closedReason]}.`
                    : "."}
                </li>
              ) : null}
            </ul>
          </section>

          {row.status !== "closed" ? (
            <section className="pfd-drawer__block">
              <h4 className="pfd-drawer__label">Close this request</h4>
              <div className="pfd-close-zone">
                <p className="pfd-close-zone__copy">
                  Closing stops it appearing in the queue. The admin sees the reason on the member
                  row.
                </p>
                <div className="pfd-close-zone__chips" role="radiogroup" aria-label="Close reason">
                  {GUARD_APP_CLOSE_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      role="radio"
                      aria-checked={closeReason === reason}
                      className="pfd-close-chip"
                      disabled={busy}
                      onClick={() => setCloseReason(reason)}
                    >
                      {GUARD_APP_CLOSE_REASON_COPY[reason]}
                    </button>
                  ))}
                </div>
                <div className="pfd-close-zone__actions">
                  <button
                    type="button"
                    className="pfd-close-go"
                    disabled={busy || closeReason === null}
                    onClick={() => {
                      if (closeReason === null) return;
                      void run(() => closeGuardAppAccessRequest(client, row.requestId, closeReason));
                    }}
                  >
                    {busy ? "Closing…" : "Close request"}
                  </button>
                  <button type="button" className="builder__navbtn" disabled={busy} onClick={onClose}>
                    Keep it open
                  </button>
                </div>
                <p className="pfd-close-zone__note">
                  A reason is required. Wrong address sends it back to the admin to correct rather
                  than ending it.
                </p>
              </div>
            </section>
          ) : null}

          {failure !== null ? (
            <p className="surface__state surface__state--error" role="alert">
              {failure}
            </p>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

function StoreStepRow({
  show,
  badge,
  email,
  addedAt,
  addedByYou,
  storeName,
  markLabel,
  closed,
  busy,
  timeZone,
  onMark,
}: {
  show: boolean;
  badge: string;
  email: string | null;
  addedAt: string | null;
  addedByYou: boolean;
  storeName: string;
  markLabel: string;
  closed: boolean;
  busy: boolean;
  timeZone: string;
  onMark: () => void;
}) {
  if (!show || email === null) return null;
  return (
    <div className="pfd-step-row">
      <div className="pfd-step-row__top">
        <span className="pfd-store-badge">{badge}</span>
        <span className="pfd-store-mail">{email}</span>
        <CopyEmailButton email={email} />
      </div>
      {addedAt !== null ? (
        <p className="pfd-step-row__state pfd-step-row__state--done">
          Added{addedByYou ? " by you" : ""} at {formatDisplayTime(addedAt, timeZone)}. {storeName}{" "}
          sends its own invite.
        </p>
      ) : (
        <>
          <p className="pfd-step-row__state">Not added yet.</p>
          {!closed ? (
            <button type="button" className="pfd-mark" disabled={busy} onClick={onMark}>
              {busy ? "Marking…" : markLabel}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
